import { isPostalCode } from "validator";

import { BaseProfile, PaymentFormErrors, PaymentInfo } from "../types";

import { Psychic } from "@/entities/Psychic";

export const birthDateInputPattern = /^(\d{0,2}|\d{0,2}\/\d{0,2}|\d{0,2}\/\d{0,2}\/\d{0,4})$/;

export const expDateInputPattern = /^(\d{0,2}|\d{0,2}\/\d{0,2})$/;

export const cardNumberInputPattern = /^\d{0,16}$/;

export const cvcInputPattern = /^\d{0,4}$/;

export const zipCodeGlobalPattern = /^[a-z0-9 ]*$/gi;

export const referralCodePattern = /^[a-zA-Z]{0,16}$/;

const nonWhitespaceRegex = /\S/;

export function validateLoginForm({ email, password }) {
  const errors: Record<string, string> = {};

  if (!email || !email.length) {
    errors.email = "Email is required";
  } else {
    // [non-empty]@[non-empty].[non-empty]
    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!email.match(emailRegex)) {
      errors.email = "Invalid email address";
    }
  }

  if (!password || !password.length) {
    errors.password = "Password is required";
  }

  return errors;
}

export function validatePaymentMethod({
  expDate,
  cardNumber,
  cvc,
  cardHolder,
  cardZipcode,
}: Omit<PaymentInfo, "termsChecked" | "plan" | "referredByCode">) {
  const errors = {} as PaymentFormErrors;

  if (!expDate || !expDate.length) {
    errors.expDate = "Expiration date is required";
  } else {
    // [2 digits]/[2 digits]
    const expDateRegex = /^\d\d\/\d\d$/;
    if (!expDate.match(expDateRegex)) {
      errors.expDate = "Expiration date must be formatted MM/YY";
    } else {
      const [expDateMonthStr, expDateYearStr] = expDate.split("/");
      const expDateMonth = parseInt(expDateMonthStr, 10);
      const expDateYear = 2000 + parseInt(expDateYearStr, 10);
      if (expDateMonth < 1 || expDateMonth > 12) {
        errors.expDate = "Expiration month is not a valid month";
      } else {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();
        if (expDateYear < currentYear || (expDateYear === currentYear && expDateMonth < currentMonth + 1)) {
          errors.expDate = "Expiration date has passed";
        }
      }
    }
  }

  if (!cardNumber || !cardNumber.length) {
    errors.cardNumber = "Card number is required";
  } else {
    // 15 or 16 digits
    const cardNumberRegex = /^\d{14,16}$/;
    if (!cardNumber.match(cardNumberRegex)) {
      errors.cardNumber = "Card number is invalid";
    }
  }

  if (!cvc || !cvc.length) {
    errors.cvc = "Security code is required";
  } else {
    // 3 or 4 digits
    const cvcRegex = /^\d{3}\d?$/;
    if (!cvc.match(cvcRegex)) {
      errors.cvc = "Security code is invalid";
    }
  }

  if (!cardHolder || !cardHolder.length) {
    errors.cardHolder = "Card holder name is required";
  } else if (!cardHolder.match(nonWhitespaceRegex)) {
    errors.cardHolder = "Card holder name is empty";
  }

  if (!cardZipcode || !cardZipcode.length) {
    errors.cardZipcode = "ZIP is required";
  } else if (
    !isPostalCode(cardZipcode, "US") &&
    !isPostalCode(cardZipcode, "CA") &&
    !isPostalCode(cardZipcode, "GB") &&
    !isPostalCode(cardZipcode, "NZ")
  ) {
    errors.cardZipcode = "ZIP is invalid";
  }

  return errors;
}

export function validateProfileStep1({ gender, birthTime, birthLocation, currentLocation, bio }: BaseProfile) {
  const errors: Record<string, string> = {};

  if (gender === "") {
    errors.gender = "Gender not selected";
  }

  if (birthTime === "") {
    errors.birthTime = "Birth time not selected";
  }

  if (birthLocation === undefined || !birthLocation.match(nonWhitespaceRegex)) {
    errors.birthLocation = "Birth location is empty";
  }

  if (currentLocation === undefined || !currentLocation.match(nonWhitespaceRegex)) {
    errors.currentLocation = "Current location is empty";
  }

  if (bio === undefined || !bio.match(nonWhitespaceRegex)) {
    errors.bio = "Bio is empty";
  }

  return errors;
}

export function validateProfileStep2({ experienceLevel, seenWriter }: BaseProfile) {
  const errors: Record<string, string> = {};

  if (!experienceLevel) {
    errors.experienceLevel = "Experience level not selected";
  }

  if (!seenWriter) {
    errors.seenWriter = "Not selected";
  }

  return errors;
}

export function validateIntuitiveProfile(profile: BaseProfile) {
  return { ...validateProfileStep1(profile), ...validateProfileStep2(profile) };
}

export function validatePsychicProfile({ firstName, lastName }: Psychic) {
  const errors: Record<string, string> = {};

  if (firstName === "") {
    errors.firstName = "First name is required";
  }

  if (lastName === "") {
    errors.lastName = "Last name is required";
  }

  return errors;
}

export function validateTip(tip: string) {
  const MIN_TIP_THRESHOLD = 0.5;
  const DECIMAL_REGEX = /^\d+(\.\d{1,2})?$/; // invalidate 0x or 0b
  const tipNum = Number(tip);

  if (!tip) {
    return ["No tip option is selected", ""];
  }
  if (!tip.match(DECIMAL_REGEX) || Number.isNaN(tipNum)) {
    return ["Invalid tip input", ""];
  }
  if (tipNum < MIN_TIP_THRESHOLD) {
    return [`Tip is lower than the minimum amount: ${MIN_TIP_THRESHOLD}`, ""];
  }
  return ["", tipNum.toFixed(2)];
}

export type NewPsychicErrors = {
  firstName?: string;
  lastName?: string;
  color?: string;
};

export function validateNewPsychic({ firstName, lastName, color }: NewPsychicErrors) {
  const errors: Record<string, string> = {};

  if (!firstName.length) {
    errors.firstName = "Required field";
  }
  if (!lastName.length) {
    errors.lastName = "Required field";
  }
  if (!color.length) {
    errors.color = "Please specify some color";
  }

  return errors;
}

export type NewTagErrors = {
  text?: string;
  color?: string;
};

export function validateNewTag({ text, color }: NewTagErrors) {
  const errors: Record<string, string> = {};

  if (!text.length) {
    errors.text = "Required field";
  }
  if (!color.length) {
    errors.color = "Please specify some color";
  }

  return errors;
}
